import React, {useEffect, useId, useMemo, useState} from 'react'
import PropTypes from 'prop-types'

// Components
import {Box, Flex} from '@chakra-ui/react'

// Others
import {useIntl} from 'react-intl'
import {StoreLocatorContext, useStoreLocator} from '../../../components/store-locator-modal'
import MapPage from '../../../components/store-locator-modal/google-maps'
import FindStoreLink from '../../../components/find-store/find-store-link'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import {
    COUNTRY_MESSAGES,
    NUM_STORES_PER_REQUEST_API_MAX,
    STORE_LOCATOR_DISTANCE,
    STORE_LOCATOR_DISTANCE_UNIT
} from '../../../constants'
import Field from '../../../components/field'
import {fillMissingCoordinates} from '../../../utils/utils'
import {useGoogleMapsApiLoader} from '../../../google-maps-api'
import {useCountry} from '../../../hooks'

const PointOfPurchaseStoreLocator = ({fields, form}) => {
    const storeLocator = useStoreLocator()
    const intl = useIntl()
    const addressFieldId = useId()
    const countryCode = useCountry()
    const [latitude, setLatitude] = useState(null)
    const [longitude, setLongitude] = useState(null)
    const [selected, setSelected] = useState(null)
    const getGoogleMapsAPI = useGoogleMapsApiLoader()

    const submitForm = async (formData) => {
        const cleanedData = await fillMissingCoordinates(formData, getGoogleMapsAPI)
        setLatitude(Number(cleanedData.latitude))
        setLongitude(Number(cleanedData.longitude))
    }

    const {data: searchStoresData} = useSearchStores(
        {
            parameters: {
                countryCode,
                latitude: latitude,
                longitude: longitude,
                locale: intl.locale,
                maxDistance: STORE_LOCATOR_DISTANCE,
                limit: NUM_STORES_PER_REQUEST_API_MAX,
                distanceUnit: STORE_LOCATOR_DISTANCE_UNIT
            }
        },
        {
            enabled: Boolean(latitude != null && longitude != null)
        }
    )

    const mappedStores = useMemo(() => {
        const countryCode = searchStoresData?.data?.[0]?.countryCode
        const country = COUNTRY_MESSAGES[countryCode]
            ? intl.formatMessage(COUNTRY_MESSAGES?.[countryCode])
            : 'Unknown country'

        return (
            searchStoresData?.data?.map((store) => ({
                id: store.id,
                name: store.name,
                position: {
                    lat: store.latitude,
                    lng: store.longitude
                },
                infoWindowHtml: store.infoWindowHtml,
                userInputRetailerName: store.name,
                userInputRetailerStreet: store.address1,
                userInputRetailerCity: store.city,
                userInputRetailerCountry: country
            })) ?? []
        )
    }, [intl, searchStoresData?.data])

    useEffect(() => {
        if (selected?.userInputRetailerName) {
            form.setValue('userInputRetailerName', selected.userInputRetailerName)
        }
        if (selected?.userInputRetailerStreet) {
            form.setValue('userInputRetailerStreet', selected.userInputRetailerStreet)
        }
        if (selected?.userInputRetailerCity) {
            form.setValue('userInputRetailerCity', selected.userInputRetailerCity)
        }
        if (selected?.userInputRetailerCountry) {
            form.setValue('userInputRetailerCountry', selected.userInputRetailerCountry)
        }
    }, [selected, form])

    return (
        <StoreLocatorContext.Provider value={storeLocator}>
            <Flex flexDirection={{base: 'column-reverse', md: 'row'}} gap={6}>
                <Flex flexGrow={1} flexDirection="column" gap={6}>
                    <FindStoreLink
                        addressFieldId={addressFieldId}
                        onSubmit={submitForm}
                        formLabel={intl.formatMessage({
                            defaultMessage: 'Where did you buy it from?',
                            id: 'warranty.pop.where_buy'
                        })}
                    />
                    <Field {...fields.userInputRetailerName} />
                    <Field {...fields.userInputRetailerStreet} />
                    <Field {...fields.userInputRetailerCity} />
                    <Field {...fields.userInputRetailerCountry} />
                </Flex>
                <Box width={{base: '100%', md: '50%'}} minWidth={{base: '100%', md: '50%'}}>
                    <MapPage
                        locations={mappedStores}
                        hideMarkerNumbers={true}
                        selected={selected}
                        onSelectedChange={setSelected}
                    />
                </Box>
            </Flex>
        </StoreLocatorContext.Provider>
    )
}

PointOfPurchaseStoreLocator.propTypes = {
    fields: PropTypes.object.isRequired,
    form: PropTypes.shape({
        setValue: PropTypes.func.isRequired
    }).isRequired
}

export default PointOfPurchaseStoreLocator
